import { FC, useEffect, useRef } from "react";
import { useIonViewDidEnter, useIonViewWillLeave } from "@ionic/react";
import { MatchPhone } from "refsix-js-models";
import {
    shouldShowRatingPrompt,
    triggerRatingPrompt,
} from "../services/ratingPromptService";
import { filterResults } from "../services/matchService";
import { useUserActionsCheckList } from "../components/hooks";
import { useMatches } from "../components/hooks/match";

// Time the user must dwell on the match result page before we surface the prompt.
const DWELL_TIME_MS = 10000;

export type MatchResultRatingPromptProps = {
    match: MatchPhone;
};

/**
 * Match-result trigger: once the user has spent DWELL_TIME_MS on a finished
 * match that (together with their recent history) meets the eligibility
 * criteria, fires the native app-store review sheet and the `reviewRequested`
 * Mixpanel event. Renders nothing — it's a side-effect-only component mounted
 * inside the match-result IonPage so the Ionic view lifecycle hooks fire.
 */
export const MatchResultRatingPrompt: FC<MatchResultRatingPromptProps> = ({
    match,
}) => {
    const timerRef = useRef<ReturnType<typeof setTimeout>>();
    const results = filterResults(useMatches() ?? []);
    const { ratePromptLastShown } = useUserActionsCheckList();

    useIonViewDidEnter(() => {
        if (!shouldShowRatingPrompt(match, results, ratePromptLastShown)) {
            return;
        }
        // Clear any pending timer first — re-entering the same view would
        // otherwise leak the previous timeout.
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        timerRef.current = setTimeout(() => {
            triggerRatingPrompt().catch((err) =>
                console.warn("ratingPrompt trigger failed", err)
            );
        }, DWELL_TIME_MS);
    });

    useIonViewWillLeave(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = undefined;
        }
    });

    // Backstop: this component is mounted inside FixtureSegment's IonContent,
    // not at the IonPage root, so a plain React unmount may not trigger
    // useIonViewWillLeave. Clear the timer on unmount to avoid firing after the
    // component has gone away.
    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, []);

    return null;
};

export default MatchResultRatingPrompt;
